import { useCallback } from "react";
import { SearchBoxListOption } from "src/components/core/SearchBox";
import {
  searchAddress,
  searchAddressesAutoComplete,
  getSecondaryLabel,
  AddressSuggestion,
} from "src/hooks/useAddressSearch";
import useComboBox from "src/hooks/useComboBox";
import { GeoLocation } from "src/types/common";
import { SmartyStreetAddress } from "src/types/neighborhoodSearch";
import { Address } from "src/types/listing";

// Regex to extract unit number from secondary address field
// Matches: "Apt 101", "Ste B", "Unit 5A", etc.
// Only extracts for recognized unit designators (Apt, Ste, Unit)
const unitDesignatorRegex = /^(Apt|Ste|Unit)\s+(.+)$/i;

/**
 * Extracts the unit number from a secondary address field.
 * Only extracts for recognized unit designators (Apt, Ste, Unit).
 * @param secondary - The secondary field (e.g., "Apt 101", "Ste B", "Unit 5")
 * @returns The unit number/identifier (e.g., "101", "B", "5") or undefined
 */
const extractUnitNumber = (secondary: string | undefined): string | undefined => {
  if (!secondary) return undefined;

  const match = secondary.match(unitDesignatorRegex);
  return match?.[2]?.trim();
};

export interface AddressSelectedData {
  address: Address;
  location: GeoLocation;
  formattedAddress: string;
  smartyStreetAddress: SmartyStreetAddress;
}

interface UseAddressSearchBoxOptions {
  onAddressSelected: (data: AddressSelectedData) => void;
  onLocationChange?: (location: GeoLocation) => void;
  onAddressCleared?: () => void;
}

export const useAddressSearchBox = ({
  onAddressSelected,
  onLocationChange,
  onAddressCleared,
}: UseAddressSearchBoxOptions) => {
  const getAddressSearchOptions = useCallback(async (query: string) => {
    const fetchedAddresses = await searchAddressesAutoComplete(query);
    return fetchedAddresses?.map((suggestion) => {
      const { streetLine, city, state, zipcode, entries, secondary } = suggestion;
      const baseLabel = `${streetLine}, ${city}, ${state}, ${zipcode}`;
      let label = baseLabel;
      if (secondary) {
        const secondaryLabel = getSecondaryLabel(secondary, entries);
        label =
          entries > 1
            ? `${baseLabel} (${entries} ${secondaryLabel})`
            : `${baseLabel} (${secondaryLabel})`;
      }
      return {
        label,
        value: `${streetLine}|${city}|${state}|${zipcode}|${secondary}|${entries}`.replace(
          /\s+/g,
          "-"
        ),
        data: suggestion,
      } as SearchBoxListOption<AddressSuggestion>;
    });
  }, []);

  const {
    options: addresses,
    value: selectedAddress,
    setValue: setSelectedAddress,
    searchTerm,
    setSearchTerm,
    isSearching,
  } = useComboBox({
    fetchOptions: getAddressSearchOptions,
  });

  const handleAddressSelected = useCallback(
    async (newSelectedAddress: SearchBoxListOption | null | undefined) => {
      setSelectedAddress(newSelectedAddress);

      // If address is cleared (null/undefined)
      if (!newSelectedAddress) {
        onAddressCleared?.();
        return;
      }

      try {
        const result = await searchAddress({
          street: newSelectedAddress.label,
        });

        if (!result) {
          return;
        }

        const { address, geoLocation, smartyStreetAddress } = result;

        // Add unit (secondary) to the address if present in the suggestion data
        // Only pass unit when entries === 1 (user selected a specific unit, not a multi-unit address)
        // Only extract unit number for recognized designators (Apt, Ste, Unit)
        const suggestion = newSelectedAddress.data as AddressSuggestion | undefined;
        const unitNumber =
          suggestion?.entries === 1 ? extractUnitNumber(suggestion?.secondary) : undefined;
        const addressWithUnit = unitNumber ? { ...address, unit: unitNumber } : address;

        const addressData: AddressSelectedData = {
          address: addressWithUnit,
          location: geoLocation,
          formattedAddress: newSelectedAddress.label,
          smartyStreetAddress,
        };

        onLocationChange?.(geoLocation);
        onAddressSelected(addressData);

        return addressData;
      } catch (error) {
        console.error("Error fetching address details", error);
      }
    },
    [onAddressSelected, onLocationChange, onAddressCleared, setSelectedAddress]
  );

  return {
    addresses,
    selectedAddress,
    setSelectedAddress,
    searchTerm,
    setSearchTerm,
    isSearching,
    handleAddressSelected,
  };
};
